import { ConfirmDialog, fmt, useToast } from "@nabble/ui";
import { deleteRole, type Role } from "../../../api/adminRoles";
import { errorMessage } from "../../../errors";

interface DeleteRoleDialogProps {
  /** The role to delete; null keeps the dialog closed. */
  target: Role | null;
  onClose: () => void;
  onDeleted: () => void;
}

function consequence(role: Role): string {
  if (role.users === 0) return `${role.name} will be deleted. This can't be undone.`;
  return `${fmt.number(role.users)} ${role.users === 1 ? "user loses" : "users lose"} the permissions of ${role.name}. This can't be undone.`;
}

/** Asks before deleting a custom role, saying how many users lose its permissions. */
export function DeleteRoleDialog({ target, onClose, onDeleted }: DeleteRoleDialogProps): React.JSX.Element {
  const { show } = useToast();

  async function remove(role: Role): Promise<void> {
    try {
      await deleteRole(role.id);
      show({ title: "Role deleted", text: `${role.name} is no longer available.`, tone: "success" });
      onDeleted();
    } catch (err) {
      show({ title: "We couldn't delete the role", text: errorMessage(err, "Please try again."), tone: "danger" });
    }
  }

  return (
    <ConfirmDialog
      open={target !== null}
      tone="danger"
      icon="trash"
      title="Delete role"
      description={target ? consequence(target) : undefined}
      confirmLabel="Delete role"
      onConfirm={() => (target ? remove(target) : undefined)}
      onClose={onClose}
    />
  );
}
